/**
 * WalledGardens — Hospitality > Walled Gardens.
 *
 * The pre-authentication allow-list for each captive-portal SSID: hosts/domains a guest
 * may reach BEFORE they authenticate (payment gateways, the portal itself, PMS, CDNs).
 * Reuses the existing site portals surface (usePortals / useSetPortal) — the walled_garden
 * lives on the portal object — so this is a focused editor over that data, per site.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { usePortals, useSetPortal } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { Portal } from "@/types/domain";

const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

export default function WalledGardens() {
  const siteId = useSession((s) => s.currentSiteId);
  const { data, isLoading, error } = usePortals(siteId);
  const [editing, setEditing] = useState<Portal | null>(null);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Walled Gardens" subtitle="Pre-auth allow-list per captive-portal SSID" />
        <EmptyState title="Select a site" hint="Walled gardens are configured per site. Pick a site in the scope bar." />
      </div>
    );
  }

  const portals = data?.portals ?? [];

  return (
    <div>
      <PageHeader title="Walled Gardens" subtitle="Hosts guests can reach before they authenticate — per captive-portal SSID" />

      {/* A walled garden only exists INSIDE a captive portal: services/portal_manager drops the
          whole `captive` block when the portal is disabled, so saving a garden for an SSID whose
          portal is off silently discards it. Verified by write test 2026-09-22. Say so, and
          disable the affordance -- offering an action that cannot work is worse than not
          offering it. */}
      <p className="mb-4 max-w-3xl text-sm text-ink-muted">
        A walled garden applies only while an SSID&rsquo;s guest portal is on. Turn the portal on
        under <span className="text-ink">Guest Portal</span> first &mdash; hosts saved for a
        disabled portal are not kept.
      </p>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading portals…</div>}

      {data && portals.length === 0 && (
        <EmptyState title="No SSIDs yet" hint="Create an SSID (and enable its captive portal under Guest Portal) to define a walled garden for it." />
      )}

      {portals.length > 0 && (
        <ul className="flex flex-col gap-2">
          {portals.map((p) => (
            <li key={p.ssid} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{p.ssid}</span>
                  {!p.enabled && (
                    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">portal off</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {!p.enabled
                    ? "Portal off — a walled garden cannot be saved until it is enabled"
                    : (p.walled_garden ?? []).length > 0
                      ? `${p.walled_garden.length} allowed host(s): ${p.walled_garden.slice(0, 3).join(", ")}${p.walled_garden.length > 3 ? "…" : ""}`
                      : "No pre-auth hosts allowed"}
                </div>
              </div>
              <RoleGate min="org_admin" mode="hide">
                <Button
                  variant="secondary"
                  onClick={() => setEditing(p)}
                  disabled={!p.enabled}
                  title={p.enabled
                    ? undefined
                    : "Turn this SSID's guest portal on first — a walled garden is only kept while the portal is enabled."}
                >
                  Edit
                </Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {editing && <GardenDialog siteId={siteId} portal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function GardenDialog({ siteId, portal, onClose }: { siteId: string; portal: Portal; onClose: () => void }) {
  const set = useSetPortal(siteId);
  // One host per line -> string[]; blank lines dropped.
  const [text, setText] = useState((portal.walled_garden ?? []).join("\n"));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const walled_garden = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    set.mutate(
      { ssid: portal.ssid, enabled: portal.enabled, auth_mode: portal.auth_mode ?? undefined, walled_garden },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Walled garden — {portal.ssid}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Hosts/domains a guest may reach before authenticating. One per line (e.g. the payment gateway, your PMS, portal assets).
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7}
              placeholder={"pay.stripe.com\n*.hotelbrand.com\ncaptive.example.net"}
              className={`${INPUT} font-mono text-xs`} />
            {set.isError && <span className="text-xs text-critical">Could not save the walled garden.</span>}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={set.isPending}>
                {set.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
